/* ==========================================================================
   Yuletide & Hearth — theme scripts
   Vanilla JS custom elements. No dependencies.
   ========================================================================== */
(() => {
  'use strict';

  const theme = window.theme || {};
  const strings = theme.strings || {};
  const routes = theme.routes || {};

  /* ---------- Utilities ---------- */

  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  };

  const fetchConfig = (body) => ({
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });

  const parseHTML = (text) => new DOMParser().parseFromString(text, 'text/html');

  function formatMoney(cents, format = theme.moneyFormat || '${{amount}}') {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = Number(cents) || 0;
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const withDelimiters = (number, precision, thousands = ',', decimal = '.') => {
      const fixed = (number / 100).toFixed(precision);
      const [whole, frac] = fixed.split('.');
      const wholeFormatted = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return frac ? wholeFormatted + decimal + frac : wholeFormatted;
    };
    const match = format.match(placeholder);
    if (!match) return String(cents / 100);
    let value;
    switch (match[1]) {
      case 'amount': value = withDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' ', ''); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': value = withDelimiters(cents, 2, ' ', '.'); break;
      default: value = withDelimiters(cents, 2);
    }
    return format.replace(placeholder, value);
  }

  /* ---------- Scroll lock + overlay ---------- */

  const overlay = $('[data-overlay]');
  let openLayers = [];

  function openLayer(el, trigger) {
    if (!el) return;
    openLayers.push({ el, trigger });
    el.classList.add('is-open');
    el.removeAttribute('hidden');
    if (overlay) overlay.hidden = false;
    document.body.classList.add('is-locked');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => {
      const focusTarget = el.querySelector('[data-autofocus]') || el;
      focusTarget.focus({ preventScroll: true });
    });
  }

  function closeLayer(el) {
    const index = openLayers.findIndex((layer) => layer.el === el);
    if (index === -1) return;
    const [{ trigger }] = openLayers.splice(index, 1);
    el.classList.remove('is-open');
    if (el.matches('.modal')) el.hidden = true;
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      trigger.focus({ preventScroll: true });
    }
    if (openLayers.length === 0) {
      if (overlay) overlay.hidden = true;
      document.body.classList.remove('is-locked');
    }
  }

  function closeTopLayer() {
    const top = openLayers[openLayers.length - 1];
    if (top) closeLayer(top.el);
  }

  overlay?.addEventListener('click', () => {
    while (openLayers.length) closeTopLayer();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeTopLayer();
  });

  // Trap focus within the top layer
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab' || openLayers.length === 0) return;
    const { el } = openLayers[openLayers.length - 1];
    const focusables = $$('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])', el)
      .filter((node) => node.offsetParent !== null);
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* ---------- Toast ---------- */

  const toastEl = $('[data-toast]');
  let toastTimer;
  function toast(message, isError = false) {
    if (!toastEl) return;
    $('[data-toast-message]', toastEl).textContent = message;
    $('.toast__icon', toastEl).textContent = isError ? 'error' : 'check_circle';
    toastEl.classList.toggle('is-error', isError);
    toastEl.hidden = false;
    toastEl.style.animation = 'none';
    // restart animation
    void toastEl.offsetWidth;
    toastEl.style.animation = '';
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { toastEl.hidden = true; }, 2800);
  }
  window.theme.toast = toast;

  /* ---------- Generic drawer / sheet triggers ---------- */

  document.addEventListener('click', (event) => {
    const openBtn = event.target.closest('[data-drawer-open]');
    if (openBtn) {
      event.preventDefault();
      openLayer(document.getElementById(openBtn.dataset.drawerOpen), openBtn);
      return;
    }
    const sheetBtn = event.target.closest('[data-sheet-open]');
    if (sheetBtn) {
      event.preventDefault();
      openLayer(document.getElementById(sheetBtn.dataset.sheetOpen), sheetBtn);
      return;
    }
    const closeBtn = event.target.closest('[data-drawer-close], [data-sheet-close], [data-modal-close]');
    if (closeBtn) {
      const layer = closeBtn.closest('.drawer, .bottom-sheet, .modal');
      if (layer) closeLayer(layer);
    }
  });

  /* ---------- Header ---------- */

  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.header = this.querySelector('.site-header');
      const setHeight = () => {
        document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
      };
      setHeight();
      window.addEventListener('resize', debounce(setHeight, 150));
      if (this.dataset.sticky === 'true') {
        const onScroll = () => this.header.classList.toggle('is-scrolled', window.scrollY > 10);
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
      }
      const toggle = this.querySelector('[data-mobile-search-toggle]');
      const mobileSearch = this.querySelector('#MobileSearch');
      toggle?.addEventListener('click', () => {
        mobileSearch.hidden = !mobileSearch.hidden;
        toggle.setAttribute('aria-expanded', String(!mobileSearch.hidden));
        if (!mobileSearch.hidden) mobileSearch.querySelector('input[type="search"]')?.focus();
        setHeight();
      });
    }
  }
  customElements.define('sticky-header', StickyHeader);

  class MenuDrawer extends HTMLElement {}
  customElements.define('menu-drawer', MenuDrawer);

  /* ---------- Announcement slider ---------- */

  class AnnouncementSlider extends HTMLElement {
    connectedCallback() {
      this.slides = $$('.announcement__slide', this);
      if (this.slides.length < 2) return;
      this.index = 0;
      const interval = Number(this.dataset.interval) || 5000;
      this.timer = setInterval(() => this.next(), interval);
      this.addEventListener('mouseenter', () => clearInterval(this.timer));
      this.addEventListener('mouseleave', () => { this.timer = setInterval(() => this.next(), interval); });
    }
    next() {
      if (this.classList.contains('announcement__slides--inline') && window.innerWidth >= 990) return;
      this.slides[this.index].classList.remove('is-active');
      this.index = (this.index + 1) % this.slides.length;
      this.slides[this.index].classList.add('is-active');
    }
    disconnectedCallback() { clearInterval(this.timer); }
  }
  customElements.define('announcement-slider', AnnouncementSlider);

  /* ---------- Localization picker ---------- */

  class LocalizationPicker extends HTMLElement {
    connectedCallback() {
      const toggle = $('[data-localization-toggle]', this);
      const list = $('[data-localization-list]', this);
      const input = $('input[type="hidden"]', this);
      const form = $('form', this);
      toggle.addEventListener('click', () => {
        list.hidden = !list.hidden;
        toggle.setAttribute('aria-expanded', String(!list.hidden));
      });
      $$('button[data-value]', list).forEach((btn) => btn.addEventListener('click', () => {
        input.value = btn.dataset.value;
        form.submit();
      }));
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) {
          list.hidden = true;
          toggle.setAttribute('aria-expanded', 'false');
        }
      });
    }
  }
  customElements.define('localization-picker', LocalizationPicker);

  /* ---------- Predictive search ---------- */

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = $('input[type="search"]', this);
      this.results = $('[data-predictive-results]', this);
      this.cache = new Map();
      this.input.addEventListener('input', debounce(() => this.onInput(), 250));
      this.input.addEventListener('focus', () => { if (this.input.value.trim()) this.open(); });
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      document.addEventListener('click', (event) => { if (!this.contains(event.target)) this.close(); });
    }

    async onInput() {
      const term = this.input.value.trim();
      if (!term) return this.close();
      if (this.cache.has(term)) return this.render(this.cache.get(term));
      this.controller?.abort();
      this.controller = new AbortController();
      try {
        const url = `${routes.predictiveSearch}?q=${encodeURIComponent(term)}&section_id=predictive-search&resources[type]=product,collection,query,page,article&resources[limit]=6`;
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error(response.status);
        const html = $('[data-predictive-content]', parseHTML(await response.text()))?.outerHTML || '';
        this.cache.set(term, html);
        this.render(html);
      } catch (error) {
        if (error.name !== 'AbortError') this.close();
      }
    }

    render(html) {
      this.results.innerHTML = html;
      this.open();
    }

    open() {
      if (!this.results.innerHTML.trim()) return;
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
    }

    onKeydown(event) {
      if (this.results.hidden) return;
      const options = $$('[role="option"]', this.results);
      if (!options.length) return;
      const current = options.findIndex((o) => o.getAttribute('aria-selected') === 'true');
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const next = event.key === 'ArrowDown'
          ? (current + 1) % options.length
          : (current - 1 + options.length) % options.length;
        options.forEach((o) => o.setAttribute('aria-selected', 'false'));
        options[next].setAttribute('aria-selected', 'true');
        options[next].scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter' && current > -1) {
        event.preventDefault();
        window.location.href = options[current].href;
      } else if (event.key === 'Escape') {
        this.close();
      }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ---------- Quantity input ---------- */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = $('input', this);
      this.addEventListener('click', (event) => {
        const btn = event.target.closest('button');
        if (!btn) return;
        event.preventDefault();
        const step = Number(this.input.step) || 1;
        const min = this.input.min !== '' ? Number(this.input.min) : 0;
        const max = this.input.max !== '' ? Number(this.input.max) : Infinity;
        const value = Number(this.input.value) || 0;
        const next = btn.name === 'plus' ? Math.min(value + step, max) : Math.max(value - step, min);
        if (next === value) return;
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- Cart ---------- */

  const Cart = {
    sectionIds() {
      return [...new Set($$('[data-cart-section]').map((el) => el.dataset.cartSection))];
    },

    async add(items, sourceButton) {
      const sections = this.sectionIds();
      const response = await fetch(`${routes.cartAdd}.js`, fetchConfig({ items, sections, sections_url: window.location.pathname }));
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || strings.error);
      if (data.sections) this.renderSections(data.sections);
      const cart = await (await fetch(`${routes.cart}.js`)).json();
      this.updateCounts(cart);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, source: sourceButton } }));
      return cart;
    },

    async change(line, quantity) {
      const sections = this.sectionIds();
      const response = await fetch(`${routes.cartChange}.js`, fetchConfig({ line, quantity, sections, sections_url: window.location.pathname }));
      const cart = await response.json();
      if (!response.ok || cart.status) throw new Error(cart.description || cart.message || strings.error);
      if (cart.sections) this.renderSections(cart.sections);
      this.updateCounts(cart);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      return cart;
    },

    async update(payload, rerender = false) {
      const body = { ...payload };
      if (rerender) body.sections = this.sectionIds();
      const response = await fetch(`${routes.cartUpdate}.js`, fetchConfig(body));
      const cart = await response.json();
      if (rerender && cart.sections) this.renderSections(cart.sections);
      return cart;
    },

    renderSections(sections) {
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const fresh = $(`[data-cart-section="${id}"]`, parseHTML(html));
        $$(`[data-cart-section="${id}"]`).forEach((current) => {
          if (!fresh) return;
          const scroller = $('.drawer__body', current);
          const scrollTop = scroller ? scroller.scrollTop : 0;
          current.innerHTML = fresh.innerHTML;
          Array.from(fresh.attributes).forEach((attr) => {
            if (attr.name !== 'class') current.setAttribute(attr.name, attr.value);
          });
          const newScroller = $('.drawer__body', current);
          if (newScroller) newScroller.scrollTop = scrollTop;
        });
      });
      CartReservation.refresh();
    },

    updateCounts(cart) {
      $$('[data-cart-count]').forEach((el) => {
        el.dataset.count = cart.item_count;
        el.textContent = cart.item_count > 0 ? cart.item_count : '';
      });
      $$('[data-cart-count-text]').forEach((el) => { el.textContent = cart.item_count; });
      $$('[data-cart-total]').forEach((el) => { el.textContent = formatMoney(cart.total_price); });
    },

    open() {
      const drawer = document.getElementById('CartDrawer');
      if (!drawer || theme.settings.cartType !== 'drawer' || $('[data-cart-page]')) {
        window.location.href = routes.cart;
        return;
      }
      openLayer(drawer);
    },
  };
  window.theme.cart = Cart;

  // Open cart drawer from any [data-cart-open] link
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-cart-open]');
    if (!trigger) return;
    if (theme.settings.cartType !== 'drawer' || $('[data-cart-page]')) return;
    event.preventDefault();
    openLayer(document.getElementById('CartDrawer'), trigger);
  });

  // Line item quantity / remove (drawer + cart page)
  document.addEventListener('change', async (event) => {
    const input = event.target.closest('[data-cart-quantity]');
    if (!input) return;
    const item = input.closest('[data-cart-item]');
    item?.classList.add('is-loading');
    try {
      await Cart.change(Number(input.dataset.cartQuantity), Number(input.value));
    } catch (error) {
      item?.classList.remove('is-loading');
      const errorEl = item && $('[data-cart-item-error]', item);
      if (errorEl) { errorEl.textContent = error.message; errorEl.hidden = false; }
      toast(error.message, true);
    }
  });

  document.addEventListener('click', async (event) => {
    const remove = event.target.closest('[data-cart-remove]');
    if (!remove) return;
    event.preventDefault();
    const item = remove.closest('[data-cart-item]');
    item?.classList.add('is-loading');
    try {
      await Cart.change(Number(remove.dataset.cartRemove), 0);
    } catch (error) {
      item?.classList.remove('is-loading');
      toast(error.message, true);
    }
  });

  // Gift wrap attribute + note
  document.addEventListener('change', (event) => {
    const giftWrap = event.target.closest('[data-gift-wrap]');
    if (giftWrap) {
      $$('[data-gift-wrap]').forEach((other) => { other.checked = giftWrap.checked; });
      Cart.update({ attributes: { 'Gift wrap': giftWrap.checked ? 'Yes' : '' } })
        .catch(() => toast(strings.error, true));
    }
  });

  const saveNote = debounce((value) => Cart.update({ note: value }), 500);
  document.addEventListener('input', (event) => {
    const note = event.target.closest('[data-cart-note]');
    if (!note) return;
    const counter = $('[data-note-count]', note.closest('.gift-note'));
    if (counter) counter.textContent = `${note.value.length}/${note.maxLength}`;
    // keep every note field (drawer + page) in sync
    $$('[data-cart-note]').forEach((other) => { if (other !== note) other.value = note.value; });
    saveNote(note.value);
  });

  // Reservation countdown (visual urgency, resets per session)
  const CartReservation = {
    start() {
      let expires = Number(sessionStorage.getItem('cartReservation') || 0);
      if (!expires || expires < Date.now()) {
        expires = Date.now() + 15 * 60 * 1000;
        try { sessionStorage.setItem('cartReservation', String(expires)); } catch (e) { /* ignore */ }
      }
      this.expires = expires;
      clearInterval(this.timer);
      this.timer = setInterval(() => this.tick(), 1000);
      this.tick();
    },
    tick() {
      const remaining = Math.max(0, this.expires - Date.now());
      const minutes = String(Math.floor(remaining / 60000)).padStart(2, '0');
      const seconds = String(Math.floor((remaining % 60000) / 1000)).padStart(2, '0');
      $$('[data-reservation-timer]').forEach((el) => { el.textContent = `${minutes}:${seconds}`; });
      if (remaining === 0) this.start();
    },
    refresh() { if ($('[data-reservation-timer]')) this.start(); },
  };
  CartReservation.refresh();

  class CartDrawer extends HTMLElement {}
  customElements.define('cart-drawer', CartDrawer);

  /* ---------- Product form (AJAX add to cart) ---------- */

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = $('form', this);
      if (!this.form) return;
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }

    async onSubmit(event) {
      event.preventDefault();
      const submitter = event.submitter || $('[type="submit"]', this.form);
      const buttons = [$('[type="submit"][name="add"]', this.form), submitter].filter(Boolean);
      const errorEl = $('[data-form-error]', this);
      if (errorEl) errorEl.hidden = true;

      // Validate required line item properties that live outside the form element
      const required = $$(`[form="${this.form.getAttribute("id")}"][required]`).find((field) => !field.value.trim());
      if (required) {
        required.focus();
        required.reportValidity?.();
        return;
      }

      const formData = new FormData(this.form);
      const id = Number(formData.get('id'));
      if (!id) return;
      const quantity = Number(formData.get('quantity') || 1);
      const properties = {};
      for (const [key, value] of formData.entries()) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && value !== '') properties[match[1]] = value;
      }
      const item = { id, quantity };
      if (Object.keys(properties).length) item.properties = properties;
      const items = [item];

      // Gift add-on checkboxes associated with this form
      if (this.form.getAttribute("id")) {
        $$(`[form="${this.form.getAttribute("id")}"][data-addon-variant]:checked`).forEach((addon) => {
          items.push({ id: Number(addon.dataset.addonVariant), quantity: 1 });
        });
      }

      buttons.forEach((btn) => btn.classList.add('is-loading'));
      try {
        await Cart.add(items, submitter);
        toast(strings.added);
        const modal = this.closest('.modal');
        if (modal) closeLayer(modal);
        Cart.open();
      } catch (error) {
        if (errorEl) {
          errorEl.textContent = error.message;
          errorEl.hidden = false;
        } else {
          toast(error.message, true);
        }
      } finally {
        buttons.forEach((btn) => btn.classList.remove('is-loading'));
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- Bundle ---------- */

  class BundleForm extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', () => this.updateTotal());
      $('[data-bundle-add]', this)?.addEventListener('click', (event) => this.addAll(event.currentTarget));
    }

    selected() { return $$('[data-bundle-variant]', this).filter((input) => input.checked); }

    updateTotal() {
      const selected = this.selected();
      const total = selected.reduce((sum, input) => sum + Number(input.dataset.price), 0);
      const compare = selected.reduce((sum, input) => sum + Number(input.dataset.compare), 0);
      const totalEl = $('[data-bundle-total]', this);
      const compareEl = $('[data-bundle-compare]', this);
      if (totalEl) totalEl.textContent = formatMoney(total);
      if (compareEl) {
        compareEl.textContent = formatMoney(compare);
        compareEl.hidden = compare <= total;
      }
    }

    async addAll(button) {
      const items = this.selected().map((input) => ({ id: Number(input.dataset.bundleVariant), quantity: 1 }));
      button.classList.add('is-loading');
      try {
        await Cart.add(items, button);
        toast(strings.added);
        Cart.open();
      } catch (error) {
        toast(error.message, true);
      } finally {
        button.classList.remove('is-loading');
      }
    }
  }
  customElements.define('bundle-form', BundleForm);

  /* ---------- Variant picker ---------- */

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse($('[data-variants]', this)?.textContent || '[]');
      this.sectionId = this.dataset.section;
      this.root = this.closest('[data-product-section]') || document;
      this.isMainProduct = !this.closest('.modal');
      this.addEventListener('change', () => this.onChange());
      this.updateAvailability();
    }

    selectedOptions() {
      const options = [];
      $$('fieldset[data-option-index], select[data-option-index]', this).forEach((group) => {
        const index = Number(group.dataset.optionIndex);
        if (group.tagName === 'SELECT') {
          options[index] = group.value;
        } else {
          const checked = $('input:checked', group);
          options[index] = checked ? checked.value : null;
          const label = $('[data-selected-value]', group);
          if (label && checked) label.textContent = checked.value;
        }
      });
      return options;
    }

    onChange() {
      const options = this.selectedOptions();
      const variant = this.variants.find((v) => v.options.every((value, i) => value === options[i]));
      this.updateAvailability();
      this.render(variant);
    }

    updateAvailability() {
      const options = this.selectedOptions();
      $$('input[data-option-index]', this).forEach((input) => {
        const index = Number(input.dataset.optionIndex);
        const candidate = [...options];
        candidate[index] = input.value;
        const available = this.variants.some((v) => v.available && v.options.every((value, i) => value === candidate[i]));
        input.classList.toggle('is-unavailable', !available);
      });
    }

    render(variant) {
      const form = document.getElementById(this.sectionId.startsWith('quick-view') ? this.sectionId : `product-form-${this.sectionId}`);
      const idInput = form && $('input[name="id"]', form);
      const addButton = form && $('[data-add-button]', form);
      const addLabel = form && $('[data-add-label]', form);
      const stickyLabel = $('[data-sticky-label]');
      const stickyAdd = $('[data-sticky-add]');

      if (!variant) {
        if (addButton) addButton.disabled = true;
        if (addLabel) addLabel.textContent = strings.unavailable;
        if (this.isMainProduct && stickyLabel) stickyLabel.textContent = strings.unavailable;
        return;
      }

      if (idInput) {
        idInput.value = variant.id;
        idInput.disabled = false;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      const priceLabel = variant.available
        ? strings.addToCartPrice.replace('[price]', formatMoney(variant.price))
        : strings.soldOut;
      if (addButton) addButton.disabled = !variant.available;
      if (addLabel) addLabel.textContent = priceLabel;

      // Price block
      const priceContainer = $('[data-price-container]', this.root);
      if (priceContainer) priceContainer.innerHTML = this.priceHTML(variant);

      // Sale badge on gallery
      const saleBadge = $('[data-sale-badge]', this.root);
      if (saleBadge) {
        const onSale = variant.compare_at_price > variant.price;
        saleBadge.hidden = !onSale;
        if (onSale) {
          const pct = Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
          saleBadge.textContent = saleBadge.textContent.replace(/\d+%/, `${pct}%`);
        }
      }

      if (this.isMainProduct) {
        // URL
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());

        // Sticky bar
        if (stickyLabel) stickyLabel.textContent = priceLabel;
        if (stickyAdd) stickyAdd.disabled = !variant.available;
        const stickyVariant = $('[data-sticky-variant]');
        if (stickyVariant) stickyVariant.textContent = variant.title;

        // Inventory note
        const inventory = $('[data-inventory-note]', this.root);
        if (inventory) inventory.innerHTML = this.inventoryHTML(variant, Number(inventory.dataset.threshold) || 10);

        // Shop Pay installments form
        $$(`form[id^="installments-form-"] input[name="id"]`, this.root).forEach((input) => {
          input.value = variant.id;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });

        // Gallery
        if (variant.media_id) {
          document.dispatchEvent(new CustomEvent('variant:media', { detail: { mediaId: variant.media_id, section: this.sectionId } }));
        }
      }
    }

    priceHTML(variant) {
      const onSale = variant.compare_at_price > variant.price;
      let html = `<div class="price price--lg${onSale ? ' price--on-sale' : ''}"><span class="price__current">${formatMoney(variant.price)}</span>`;
      if (onSale) {
        html += `<s class="price__compare">${formatMoney(variant.compare_at_price)}</s>`;
        html += `<span class="price__save">${strings.saveAmount.replace('[amount]', formatMoney(variant.compare_at_price - variant.price))}</span>`;
      }
      return `${html}</div>`;
    }

    inventoryHTML(variant, threshold) {
      const note = (modifier, text) => `<p class="inventory-note${modifier ? ` inventory-note--${modifier}` : ''}"><span class="dot"></span>${text}</p>`;
      if (!variant.available) return note('out', strings.outOfStock);
      if (variant.inventory_management === 'shopify' && variant.inventory_quantity > 0 && variant.inventory_quantity <= threshold) {
        return note('low', strings.lowStock.replace('[count]', variant.inventory_quantity));
      }
      return note('', strings.inStock);
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ---------- Product gallery ---------- */

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = $('[data-gallery-viewport]', this);
      this.slides = $$('.gallery__slide', this.viewport);
      this.section = this.dataset.section;
      this.thumbs = $$(`[data-gallery-thumbs="${this.section}"] [data-thumb-index]`);
      this.dots = $$(`[data-gallery-dots="${this.section}"] .gallery__dot`);
      this.counter = $('[data-gallery-current]', this);
      this.current = 0;

      this.viewport.addEventListener('scroll', debounce(() => this.syncFromScroll(), 60), { passive: true });
      $('[data-gallery-prev]', this)?.addEventListener('click', () => this.goTo(this.current - 1));
      $('[data-gallery-next]', this)?.addEventListener('click', () => this.goTo(this.current + 1));
      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.thumbIndex))));
      this.viewport.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.goTo(this.current + 1);
        if (event.key === 'ArrowLeft') this.goTo(this.current - 1);
      });
      this.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-lightbox-open]');
        if (trigger) Lightbox.open(this.slides, Number(trigger.dataset.lightboxOpen));
      });
      document.addEventListener('variant:media', (event) => {
        if (event.detail.section !== this.section) return;
        const index = this.slides.findIndex((slide) => Number(slide.dataset.mediaId) === event.detail.mediaId);
        if (index > -1) this.goTo(index);
      });
    }

    goTo(index) {
      if (!this.slides.length) return;
      const target = (index + this.slides.length) % this.slides.length;
      this.viewport.scrollTo({ left: this.slides[target].offsetLeft, behavior: 'smooth' });
      this.setActive(target);
    }

    syncFromScroll() {
      const index = Math.round(this.viewport.scrollLeft / this.viewport.clientWidth);
      this.setActive(Math.min(Math.max(index, 0), this.slides.length - 1));
    }

    setActive(index) {
      this.current = index;
      if (this.counter) this.counter.textContent = index + 1;
      this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    }
  }
  customElements.define('product-gallery', ProductGallery);

  const Lightbox = {
    open(slides, start) {
      this.images = slides.map((slide) => $('img', slide)).filter(Boolean).map((img) => {
        const sources = (img.getAttribute('srcset') || '').split(',').map((s) => s.trim().split(' ')[0]).filter(Boolean);
        return { src: sources[sources.length - 1] || img.src, alt: img.alt };
      });
      if (!this.images.length) return;
      this.index = Math.min(start, this.images.length - 1);
      this.el = document.createElement('div');
      this.el.className = 'lightbox';
      this.el.setAttribute('role', 'dialog');
      this.el.setAttribute('aria-modal', 'true');
      this.el.tabIndex = -1;
      this.el.innerHTML = `
        <img alt="">
        <button type="button" class="close-btn lightbox__close" aria-label="Close"><span class="icon">close</span></button>
        ${this.images.length > 1 ? '<button type="button" class="close-btn lightbox__nav lightbox__nav--prev" aria-label="Previous"><span class="icon">chevron_left</span></button><button type="button" class="close-btn lightbox__nav lightbox__nav--next" aria-label="Next"><span class="icon">chevron_right</span></button>' : ''}`;
      document.body.appendChild(this.el);
      document.body.classList.add('is-locked');
      this.show();
      this.el.addEventListener('click', (event) => {
        if (event.target.closest('.lightbox__nav--prev')) return this.step(-1);
        if (event.target.closest('.lightbox__nav--next')) return this.step(1);
        if (event.target.closest('.lightbox__close') || event.target === this.el) this.close();
      });
      this.onKey = (event) => {
        if (event.key === 'Escape') this.close();
        if (event.key === 'ArrowRight') this.step(1);
        if (event.key === 'ArrowLeft') this.step(-1);
      };
      document.addEventListener('keydown', this.onKey);
      let startX = 0;
      this.el.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      this.el.addEventListener('touchend', (e) => {
        const dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 50) this.step(dx < 0 ? 1 : -1);
      });
      this.el.focus();
    },
    step(delta) {
      this.index = (this.index + delta + this.images.length) % this.images.length;
      this.show();
    },
    show() {
      const img = $('img', this.el);
      img.src = this.images[this.index].src;
      img.alt = this.images[this.index].alt;
    },
    close() {
      document.removeEventListener('keydown', this.onKey);
      this.el?.remove();
      if (!openLayers.length) document.body.classList.remove('is-locked');
    },
  };

  /* ---------- Sticky add to cart ---------- */

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const form = document.getElementById(this.dataset.form);
      const addButton = form && $('[data-add-button]', form);
      if (!addButton) return;
      const observer = new IntersectionObserver(([entry]) => {
        const visible = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', visible);
        this.setAttribute('aria-hidden', String(!visible));
        $$('button, input', this).forEach((el) => { el.tabIndex = visible ? 0 : -1; });
      });
      observer.observe(addButton);

      const stickyQty = $('[data-sticky-quantity]', this);
      const mainQty = form && $('[data-main-quantity]', form);
      stickyQty?.addEventListener('change', () => { if (mainQty) mainQty.value = stickyQty.value; });
      mainQty?.addEventListener('change', () => { if (stickyQty) stickyQty.value = mainQty.value; });
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* ---------- Share ---------- */

  class ShareButton extends HTMLElement {
    connectedCallback() {
      const btn = $('button', this);
      btn.addEventListener('click', async () => {
        const url = btn.dataset.shareUrl || window.location.href;
        if (navigator.share) {
          try { await navigator.share({ title: btn.dataset.shareTitle, url }); } catch (e) { /* cancelled */ }
          return;
        }
        try {
          await navigator.clipboard.writeText(url);
          toast(strings.copied);
        } catch (e) {
          toast(url);
        }
      });
    }
  }
  customElements.define('share-button', ShareButton);

  /* ---------- Copy discount code ---------- */

  class CopyCode extends HTMLElement {
    connectedCallback() {
      $('[data-copy]', this)?.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(this.dataset.code);
          toast(strings.copied);
        } catch (e) {
          toast(this.dataset.code);
        }
      });
    }
  }
  customElements.define('copy-code', CopyCode);

  /* ---------- Quick view ---------- */

  class QuickViewModal extends HTMLElement {
    async show(url, trigger) {
      const content = $('[data-quick-view-content]', this);
      content.innerHTML = '<div class="quick-view__loading"><div class="spinner"></div></div>';
      this.hidden = false;
      openLayer(this, trigger);
      try {
        const cleanUrl = url.split('?')[0];
        const response = await fetch(`${cleanUrl}?section_id=quick-view`);
        const doc = parseHTML(await response.text());
        const view = $('.quick-view', doc);
        content.innerHTML = view ? view.outerHTML : '';
        // Scripts in fetched HTML don't run, but custom elements upgrade automatically.
      } catch (error) {
        content.innerHTML = `<p class="quick-view__loading">${strings.error}</p>`;
      }
    }
  }
  customElements.define('quick-view-modal', QuickViewModal);

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-quick-view]');
    if (!trigger) return;
    event.preventDefault();
    $('[data-quick-view-modal]')?.show(trigger.dataset.quickView, trigger);
  });

  /* ---------- Wishlist ---------- */

  const Wishlist = {
    key: 'yuletide:wishlist',
    get() {
      try { return JSON.parse(localStorage.getItem(this.key)) || []; } catch (e) { return []; }
    },
    set(list) {
      try { localStorage.setItem(this.key, JSON.stringify(list)); } catch (e) { /* storage unavailable */ }
      this.sync();
      document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { list } }));
    },
    has(handle) { return this.get().includes(handle); },
    toggle(handle) {
      const list = this.get();
      const index = list.indexOf(handle);
      if (index > -1) list.splice(index, 1); else list.unshift(handle);
      this.set(list);
      return index === -1;
    },
    sync() {
      const list = this.get();
      $$('[data-wishlist-toggle]').forEach((btn) => {
        const active = list.includes(btn.dataset.wishlistToggle);
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', String(active));
      });
      $$('[data-wishlist-count]').forEach((el) => {
        el.dataset.count = list.length;
        el.textContent = list.length > 0 ? list.length : '';
      });
    },
  };
  window.theme.wishlist = Wishlist;

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    event.preventDefault();
    event.stopPropagation();
    const added = Wishlist.toggle(btn.dataset.wishlistToggle);
    btn.classList.remove('is-bump');
    void btn.offsetWidth;
    btn.classList.add('is-bump');
    toast(added ? strings.wishlistAdded : strings.wishlistRemoved);
  });

  class WishlistPage extends HTMLElement {
    connectedCallback() {
      this.grid = $('[data-wishlist-grid]', this);
      this.empty = $('[data-wishlist-empty]', this);
      this.loading = $('[data-wishlist-loading]', this);
      this.render();
      document.addEventListener('wishlist:updated', () => this.render());
    }

    async render() {
      const list = Wishlist.get();
      if (!list.length) {
        this.grid.innerHTML = '';
        this.loading.hidden = true;
        this.empty.hidden = false;
        return;
      }
      this.empty.hidden = true;
      const cards = await Promise.all(list.map(async (handle) => {
        try {
          const response = await fetch(`${routes.root.replace(/\/$/, '')}/products/${encodeURIComponent(handle)}?section_id=wishlist-card`);
          if (!response.ok) return null;
          return $('.card', parseHTML(await response.text()))?.outerHTML || null;
        } catch (e) {
          return null;
        }
      }));
      this.loading.hidden = true;
      const html = cards.filter(Boolean).join('');
      this.grid.innerHTML = html;
      this.empty.hidden = Boolean(html);
      Wishlist.sync();
    }
  }
  customElements.define('wishlist-page', WishlistPage);

  /* ---------- Collection filtering ---------- */

  const Facets = {
    init() {
      this.root = $('[data-facets-root]');
      if (!this.root) return;
      this.sectionId = this.root.dataset.section;

      this.root.addEventListener('change', (event) => {
        const form = event.target.closest('[data-facet-form]');
        if (form) {
          if (event.target.type === 'number') return; // handled on input debounce
          // Radio pills: reflect active state immediately
          if (event.target.name === 'sort_by' && event.target.type === 'radio') {
            $$('input[name="sort_by"]', form).forEach((radio) => radio.closest('.pill')?.classList.toggle('is-active', radio.checked));
          }
          this.submitForm(form);
        }
        if (event.target.matches('[data-sort-select]')) {
          const activeForm = this.activeForm();
          if (activeForm) {
            const hidden = $('[data-sort-hidden]', activeForm) || $('input[name="sort_by"]', activeForm);
            if (hidden) hidden.value = event.target.value;
            this.submitForm(activeForm);
          } else {
            const url = new URL(window.location.href);
            url.searchParams.set('sort_by', event.target.value);
            url.searchParams.delete('page');
            this.render(url.searchParams.toString());
          }
        }
      });

      this.root.addEventListener('input', debounce((event) => {
        if (event.target.type !== 'number') return;
        const form = event.target.closest('[data-facet-form]');
        if (form) this.submitForm(form);
      }, 700));

      this.root.addEventListener('click', (event) => {
        const link = event.target.closest('[data-facet-link]');
        if (!link) return;
        event.preventDefault();
        const url = new URL(link.href, window.location.origin);
        this.render(url.searchParams.toString(), link.closest('.pagination') !== null);
      });

      window.addEventListener('popstate', () => this.render(window.location.search.slice(1), false, false));

      // Grid view toggle
      const toggle = $('[data-view-toggle]', this.root);
      const saved = (() => { try { return localStorage.getItem('yuletide:grid-view'); } catch (e) { return null; } })();
      if (saved) this.setView(saved);
      toggle?.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-view]');
        if (!btn) return;
        this.setView(btn.dataset.view);
        try { localStorage.setItem('yuletide:grid-view', btn.dataset.view); } catch (e) { /* ignore */ }
      });
    },

    activeForm() {
      const sheet = $('.bottom-sheet.is-open [data-facet-form]', this.root);
      return sheet || $('[data-facet-form][data-mode="sidebar"]', this.root) || $('[data-facet-form]', this.root);
    },

    setView(view) {
      this.view = view;
      $$('[data-product-grid]', this.root).forEach((grid) => { grid.dataset.view = view; });
      $$('[data-view-toggle] [data-view]', this.root).forEach((btn) => btn.setAttribute('aria-pressed', String(btn.dataset.view === view)));
    },

    submitForm(form) {
      const params = new URLSearchParams(new FormData(form));
      // Drop empty price inputs so they don't register as active filters
      for (const [key, value] of Array.from(params.entries())) {
        if (value === '') params.delete(key);
      }
      const sortSelect = $('[data-sort-select]', this.root);
      if (!params.has('sort_by') && sortSelect) params.set('sort_by', sortSelect.value);
      params.delete('page');
      this.render(params.toString());
    },

    async render(query, scrollToTop = false, pushState = true) {
      const gridRegion = $('[data-facet-replace="grid"]', this.root);
      gridRegion?.classList.add('is-loading');
      const openDetails = $$('details[open][id]', this.root).map((d) => d.id);
      const closedDetails = $$('details:not([open])[id]', this.root).map((d) => d.id);
      try {
        const response = await fetch(`${window.location.pathname}?${query}${query ? '&' : ''}section_id=${this.sectionId}`);
        const doc = parseHTML(await response.text());
        $$('[data-facet-replace]', this.root).forEach((region) => {
          const fresh = $(`[data-facet-replace="${region.dataset.facetReplace}"]`, doc);
          if (fresh) region.innerHTML = fresh.innerHTML;
        });
        // Restore accordion state so filters don't collapse while shopping
        openDetails.forEach((id) => document.getElementById(id)?.setAttribute('open', ''));
        closedDetails.forEach((id) => document.getElementById(id)?.removeAttribute('open'));
        if (this.view) this.setView(this.view);
        const sortSelect = $('[data-sort-select]', this.root);
        const sortValue = new URLSearchParams(query).get('sort_by');
        if (sortSelect && sortValue) sortSelect.value = sortValue;
        if (pushState) window.history.pushState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
        Wishlist.sync();
        if (scrollToTop) {
          const top = this.root.querySelector('.collection-layout, [data-facet-replace="grid"]');
          top?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } catch (error) {
        toast(strings.error, true);
      } finally {
        $('[data-facet-replace="grid"]', this.root)?.classList.remove('is-loading');
      }
    },
  };

  class FacetsSheet extends HTMLElement {}
  customElements.define('facets-sheet', FacetsSheet);

  /* ---------- Horizontal scroller arrows ---------- */

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-scroller-prev], [data-scroller-next]');
    if (!btn) return;
    const id = btn.dataset.scrollerPrev || btn.dataset.scrollerNext;
    const scroller = document.getElementById(id);
    if (!scroller) return;
    const direction = btn.hasAttribute('data-scroller-next') ? 1 : -1;
    scroller.scrollBy({ left: direction * scroller.clientWidth * 0.8, behavior: 'smooth' });
  });

  /* ---------- Product recommendations ---------- */

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      const observer = new IntersectionObserver(async ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        try {
          const response = await fetch(this.dataset.url);
          const fresh = $('product-recommendations', parseHTML(await response.text()));
          if (fresh && fresh.innerHTML.trim()) {
            this.innerHTML = fresh.innerHTML;
            Wishlist.sync();
          }
        } catch (e) { /* ignore */ }
      }, { rootMargin: '0px 0px 400px 0px' });
      observer.observe(this);
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- Delivery countdown ---------- */

  function initCountdowns() {
    const els = $$('[data-countdown]');
    if (!els.length) return;
    const cutoffHour = Number(theme.settings.countdownHour ?? 15);
    const cutoffDate = theme.settings.cutoffDate ? new Date(`${theme.settings.cutoffDate}T23:59:59`) : null;
    const tick = () => {
      const now = new Date();
      const target = new Date(now);
      target.setHours(cutoffHour, 0, 0, 0);
      if (target <= now) target.setDate(target.getDate() + 1);
      if (cutoffDate && target > cutoffDate && cutoffDate > now) target.setTime(cutoffDate.getTime());
      const diff = Math.max(0, target - now);
      const hours = Math.floor(diff / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      els.forEach((el) => { el.textContent = `${hours}h ${String(minutes).padStart(2, '0')}m`; });
    };
    tick();
    setInterval(tick, 30000);
  }

  /* ---------- Init ---------- */

  function init() {
    Wishlist.sync();
    Facets.init();
    initCountdowns();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor support
  document.addEventListener('shopify:section:load', () => {
    Wishlist.sync();
    initCountdowns();
    if ($('[data-facets-root]')) Facets.init();
  });
  document.addEventListener('shopify:section:select', (event) => {
    if (event.target.querySelector('#CartDrawer')) openLayer(document.getElementById('CartDrawer'));
  });
})();
